/**
 * The "Move to live now" picker: which live element to replace, or raise the cap
 * instead. A rotation rule's verdict arrives preselected, never locked.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AlertTriangle, Gauge } from 'lucide-react';
import type { PromoteCandidate, PromotePlanResult } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

export type PromoteChoice =
  | { kind: 'replace'; victimBankId: string }
  | { kind: 'raise-cap' };

const score = (c: PromoteCandidate) =>
  c.score === null ? '—' : c.score.toFixed(1);

const CapLine: FC<{ plan: PromotePlanResult }> = ({ plan }) => (
  <p className="flex items-center gap-1.5 text-xs">
    <Gauge className="h-3.5 w-3.5 shrink-0" aria-hidden />
    <span>
      <span className="font-medium text-foreground">{plan.poolName}</span> is at
      capacity{' '}
      <span className="tabular-nums font-medium text-foreground">
        {plan.active}/{plan.cap}
      </span>
    </span>
  </p>
);

export interface ChooseBodyProps {
  plan: PromotePlanResult;
  choice: PromoteChoice | null;
  onChoice: (next: PromoteChoice) => void;
  /** How many rule groups share this cap; 0 = the cap is precise. */
  sharedGroups: number;
  canRaiseCap: boolean;
}

/** mode === 'choose' — nothing is marked, so the user decides. */
export const ChooseBody: FC<ChooseBodyProps> = ({
  plan,
  choice,
  onChoice,
  sharedGroups,
  canRaiseCap,
}) => (
  <div className="space-y-3">
    <CapLine plan={plan} />
    <p className="text-xs">
      {plan.victim ? (
        <>
          A rotation rule suggests replacing{' '}
          <span className="font-medium text-foreground">
            {plan.victim.itemName}
          </span>
          , but you can pick another — or make room instead.
        </>
      ) : (
        <>
          No rotation rule marks anything for removal, so pick what{' '}
          <span className="font-medium text-foreground">{plan.itemName}</span>{' '}
          should replace — or make room for it.
        </>
      )}
    </p>
    <div className="space-y-1">
      {(plan.candidates ?? []).map((c) => {
        const active =
          choice?.kind === 'replace' && choice.victimBankId === c.bankId;
        return (
          <label
            key={c.bankId}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-2 text-xs hover:border-primary/40',
              active ? 'border-primary bg-primary/5' : 'border-border/60',
            )}
          >
            <input
              type="radio"
              name="promote-victim"
              className="h-3.5 w-3.5"
              checked={active}
              onChange={() =>
                onChoice({ kind: 'replace', victimBankId: c.bankId })
              }
            />
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium text-foreground">{c.itemName}</span>
              {c.reason ? (
                <span className="block truncate text-[10px] text-muted-foreground">
                  “{c.reason}”
                </span>
              ) : null}
            </span>
            {c.ruleMarked ? (
              <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-600">
                rule-marked
              </span>
            ) : null}
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {score(c)}
            </span>
          </label>
        );
      })}
    </div>
    <p className="text-[11px] text-muted-foreground">
      Whichever you replace moves to <b>Waiting</b> — nothing is eliminated, so
      you can bring it back at any time.
    </p>
    {canRaiseCap ? (
      <label
        className={cn(
          'flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-2 text-xs hover:border-primary/40',
          choice?.kind === 'raise-cap'
            ? 'border-primary bg-primary/5'
            : 'border-border/60',
        )}
      >
        <input
          type="radio"
          name="promote-victim"
          className="mt-0.5 h-3.5 w-3.5"
          checked={choice?.kind === 'raise-cap'}
          onChange={() => onChoice({ kind: 'raise-cap' })}
        />
        <span className="min-w-0">
          <span className="font-medium text-foreground">
            Raise the cap to {(plan.cap ?? 0) + 1} instead
          </span>
          {sharedGroups > 1 ? (
            <span className="mt-1 flex items-start gap-1 text-amber-600">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
              Shared by all {sharedGroups} rule groups — each may then hold{' '}
              {(plan.cap ?? 0) + 1} paths.
            </span>
          ) : (
            <span className="mt-0.5 block text-muted-foreground">
              Affects {plan.poolName} only. Saves the cap and goes live in one
              step — nothing is replaced.
            </span>
          )}
        </span>
      </label>
    ) : null}
  </div>
);
